/**
 * Facebook Conversion API fbc 参数值验证工具
 *
 * fbc 格式: fb.subdomainIndex.creationTime.fbclid
 * - version: 固定为 "fb"
 * - subdomainIndex: 域名索引数字
 * - creationTime: UNIX 时间戳(毫秒)
 * - fbclid: Facebook 点击 ID
 */
import { getValidFbc } from './fbcRegex';

/**
 * 从 Cookie 中获取 _fbc 值
 * @returns {string|null} - Cookie 中的 _fbc 值，如果不存在则返回 null
 */
export function getFbcFromCookie() {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith('_fbc=')) {
      return cookie.substring('_fbc='.length, cookie.length);
    }
  }
  return null;
}

/**
 * 从 URL 或 Cookie 中提取 fbclid 并生成标准格式的 fbc 值
 * @param {string} [fbclid] - 可选的 fbclid 值，如果不提供则尝试从 URL 获取
 * @param {number} [subdomainIndex=1] - 子域名索引，默认为 1
 * @returns {string|null} - 格式化的 fbc 值，如果没有 fbclid 则返回 null
 */
export function generateFbc(fbclid, subdomainIndex = 1) {
  // 如果没有提供 fbclid，尝试从 URL 获取
  if (!fbclid) {
    const urlParams = new URLSearchParams(window.location.search);
    fbclid = urlParams.get('fbclid');
  }

  // 如果没有 fbclid，返回 null
  if (!fbclid) {
    return null;
  }

  // 获取当前时间戳（毫秒）
  const creationTime = Date.now();

  // 生成标准格式的 fbc 值
  return `fb.${subdomainIndex}.${creationTime}.${fbclid}`;
}

/**
 * 设置 _fbc Cookie
 * @param {string} fbcValue - 要设置的 fbc 值
 * @param {number} [maxAgeDays=88] - Cookie 的有效期（天数）
 */
export function setFbcCookie(fbcValue, maxAgeDays = 88) {
  if (!fbcValue) return;

  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + maxAgeDays);

  // 设置 Cookie，path=/ 表示整个网站可用
  document.cookie = `_fbc=${fbcValue};expires=${expirationDate.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * 获取有效的 fbc 值，优先从 Cookie 获取，如果不存在或已过期则根据 URL 中的 fbclid 生成
 * 如果 URL 中有 fbclid，会自动将生成的 fbc 值存储到 Cookie 中，有效期 88 天
 * @returns {string|null} - 有效的 fbc 参数值，如果无法获取则返回 null
 */
export function getValidFbcValue() {
  // 优先从 Cookie 获取
  const fbcFromCookie = getFbcFromCookie();

  // 验证 Cookie 中的 fbc 值是否有效（包括检查是否过期）
  const validFbc = getValidFbc(fbcFromCookie);
  if (validFbc) {
    return validFbc;
  }

  // 检查 URL 中是否有 fbclid 参数
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');

  if (fbclid) {
    // 如果 URL 中有 fbclid，生成新的 fbc 值
    const newFbc = generateFbc(fbclid);

    // 将生成的 fbc 值存储到 Cookie 中，有效期 88 天
    setFbcCookie(newFbc, 88);

    return newFbc;
  }

  return null;
}
